
import { useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getLogger } from '@/utils/logger';
import { clearStaleAuthData } from '@/utils/auth-cleanup';

const log = getLogger({ module: 'AuthInitialization' });

export const useAuthInitialization = (
  fetchProfile: (userId: string) => Promise<any>,
  dispatch: Function
) => {
  const initCompletedRef = useRef(false);
  const isMountedRef = useRef(true);
  const initializationRunningRef = useRef(false);

  // Initialize auth state
  useEffect(() => {
    log.info('Starting auth initialization', new Date().toISOString());

    // Reset completion state on each initialization attempt
    initCompletedRef.current = false;

    const initializeAuth = async () => {
      // Prevent multiple simultaneous initialization attempts
      if (initializationRunningRef.current) {
        log.info('Initialization already running, skipping duplicate attempt');
        return;
      }
      
      initializationRunningRef.current = true;
      
      try {
        log.info('Checking for existing session');
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          log.error('Error getting session during initialization:', error);
          throw error;
        }

        const session = data.session;

        // If not mounted anymore, don't proceed
        if (!isMountedRef.current) return;

        if (session) {
          log.info('Session found during initialization', {
            userId: session.user.id,
            expiresAt: session.expires_at
          });

          // Validate session is working at database level
          try {
            log.info('Validating session with database');
            const { data: authCheck, error: authError } = await supabase
              .from('profiles')
              .select('id')
              .eq('id', session.user.id)
              .maybeSingle();

            if (authError) {
              log.error('Session validation failed - auth.uid() not working:', authError);
              // Session is stale/invalid, clean it up
              await supabase.auth.signOut({ scope: 'local' });
              clearStaleAuthData();
              dispatch({ type: 'SET_INITIALIZED', payload: true });
              dispatch({ type: 'SET_LOADING', payload: false });
              return;
            }
            
            log.info('Session validation successful');
          } catch (validationError) {
            log.error('Session validation error:', validationError);
            // Clean up potentially stale session
            await supabase.auth.signOut({ scope: 'local' });
            clearStaleAuthData();
            dispatch({ type: 'SET_INITIALIZED', payload: true });
            dispatch({ type: 'SET_LOADING', payload: false });
            return;
          }

          try {
            // First set the user object
            const user = {
              id: session.user.id,
              email: session.user.email
            };

            dispatch({ type: 'SET_USER', payload: user });

            log.info('Fetching user profile during initialization');
            let profile = null;
            try {
              profile = await fetchProfile(session.user.id);
              if (!isMountedRef.current) return;
              log.info('Profile fetch result during initialization:', profile);
              
              // Check if user is banned
              if (profile && profile.is_banned) {
                log.warn('User is banned, signing out');
                // Sign out the banned user
                const { error: signOutError } = await supabase.auth.signOut();
                if (signOutError) {
                  log.error('Error signing out banned user:', signOutError);
                }
                
                // Redirect to banned page
                if (typeof window !== 'undefined') {
                  window.location.href = '/banned';
                }
                return;
              }
            } catch (fetchError) {
              log.error('Profile fetch error during initialization:', fetchError);
              // Even in case of profile fetch error, don't throw, let initialization complete
            }

            if (profile && isMountedRef.current) {
              log.info('Profile found during initialization', {
                username: profile.username,
                ageVerified: !!profile.age_verified_at,
                matureContentEnabled: !!profile.mature_content_enabled,
                hasProfileImage: !!profile.profile_image_url
              });

              // Load mature content settings from profile
              try {
                const { default: MatureContentService } = await import('@/services/mature-content-service');
                const settings = {
                  ageVerified: !!profile.age_verified_at,
                  matureContentEnabled: !!profile.mature_content_enabled
                };
                MatureContentService.setLocalSettings(settings);
                log.info('Restored mature content settings from profile during initialization', settings);
              } catch (error) {
                log.error('Error setting mature content settings during initialization', error);
              }

              if (profile.username) {
                const userWithUsername = {
                  ...user,
                  username: profile.username
                };
                dispatch({ type: 'SET_USER', payload: userWithUsername });
              }

              dispatch({ type: 'SET_PROFILE', payload: profile });
            } else {
              log.info('No profile found during initialization');
            }
          } catch (error) {
            log.error('Error fetching profile during initialization:', error);
            // Do not throw, allow initialization to complete
          }
        } else {
          log.info('No session found during initialization');
        }

        if (isMountedRef.current) {
          log.info('Setting initialization complete', new Date().toISOString());
          dispatch({ type: 'SET_INITIALIZED', payload: true });
          dispatch({ type: 'SET_LOADING', payload: false });
        }

      } catch (error) {
        log.error('Auth initialization error caught by catch:', error);
        // Even on error, we mark as initialized so the app doesn't get stuck
        if (isMountedRef.current) {
          dispatch({ type: 'SET_ERROR', payload: error });
          dispatch({ type: 'SET_INITIALIZED', payload: true });
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }

      if (isMountedRef.current) {
        initCompletedRef.current = true;
      }
      
      initializationRunningRef.current = false;
    };

    initializeAuth();

    return () => {
      isMountedRef.current = false;
      initializationRunningRef.current = false;
    };
  }, [dispatch, fetchProfile]);

  return { initCompletedRef };
};
